import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import {Lightbox} from '@Components/lightbox/lightbox';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Button, Size, Type} from '@Components/button';
import {Text, TextSize} from '@Components/text';
import {ClickableDiv} from '@Components/clickable-div';
import {downloadBlobAsFile} from '@Utils/file.util';
import type {PdfLightboxProps} from './pdf-lightbox.types';
import styles from './pdf-lightbox.module.scss';

function resolveDocumentTitle(title?: string): string {
  if (title !== undefined && title !== '') {
    return title;
  }

  return window.i18next.t('pmwjs_pdf_document');
}

export function PdfLightbox({isOpen, url, title, onClose}: PdfLightboxProps): ReactElement | null {
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return (): void => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || url === undefined || url === '') {
    return null;
  }

  const documentTitle = resolveDocumentTitle(title);

  const openInNewTab = (): void => {
    window.open(url, '_blank', 'noopener');
  };

  const download = async (): Promise<void> => {
    setIsDownloading(true);
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      downloadBlobAsFile(blob, `${documentTitle}.pdf`);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Lightbox isOpen={isOpen} onClose={onClose}>
      <div className={styles.lightboxHeader}>
        <div className={styles.left}>
          <Icon
            icon="icon-arrow-left"
            size={IconSize.SIZE_ICON_16}
            type={IconType.DARK_GHOST}
            shape={IconShape.SQUARE}
            ariaLabel={window.i18next.t('pmwjs_back')}
            onClick={onClose}
            className={styles.backButton}
          />
          <Text val={documentTitle} size={TextSize.MD} bold className={styles.title} />
        </div>
        <div className={styles.headerActions}>
          <Button text={window.i18next.t('pmwjs_open_in_new_tab')} size={Size.XSMALL} type={Type.DARK_GHOST} icon="icon-external-link" onClick={openInNewTab} />
          <Button
            text={window.i18next.t('pmwjs_download')}
            size={Size.XSMALL}
            type={Type.SECONDARY}
            icon="icon-download"
            isLoading={isDownloading}
            disabled={isDownloading}
            onClick={(): void => {
              void download();
            }}
          />
        </div>
      </div>
      <ClickableDiv
        className={styles.mediaContainer}
        enableFocusOutline={false}
        onClick={(e): void => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <iframe className={styles.iframe} src={`${url}#toolbar=0&navpanes=0&view=FitH`} title={documentTitle} />
      </ClickableDiv>
    </Lightbox>
  );
}
